import { useState, useEffect } from 'react';
import { auditAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { 
  Shield, ChevronLeft, ChevronRight, Filter, RefreshCw, Trash2,
  CheckCircle, XCircle, LogIn, LogOut, UserPlus, UserMinus, Settings, Key
} from 'lucide-react';

const ACTION_ICONS = {
  login_success: LogIn,
  login_failed: LogIn,
  login_webauthn: LogIn,
  logout: LogOut,
  user_create: UserPlus,
  user_update: UserPlus,
  user_delete: UserMinus,
  user_role_change: UserPlus,
  user_subscription_change: UserPlus,
  config_smtp_update: Settings,
  config_stripe_update: Settings,
  config_subscription_update: Settings,
  api_key_create: Key,
  api_key_delete: Key,
};

const ACTION_LABELS = {
  login_success: 'Login riuscito',
  login_failed: 'Login fallito',
  login_webauthn: 'Login biometrico',
  logout: 'Logout',
  otp_verified: 'OTP verificato',
  otp_failed: 'OTP fallito',
  password_reset_request: 'Richiesta reset password',
  password_reset_complete: 'Reset password completato',
  user_create: 'Utente creato',
  user_update: 'Utente modificato',
  user_delete: 'Utente eliminato',
  user_role_change: 'Ruolo modificato',
  user_subscription_change: 'Abbonamento modificato',
  obc_create: 'OBC creato',
  obc_update: 'OBC modificato',
  obc_delete: 'OBC eliminato',
  token_create: 'Token creato',
  token_delete: 'Token eliminato',
  token_redeem: 'Token riscattato',
  config_smtp_update: 'Config SMTP aggiornata',
  config_stripe_update: 'Config Stripe aggiornata',
  config_subscription_update: 'Config abbonamenti aggiornata',
  api_key_create: 'API Key creata',
  api_key_delete: 'API Key eliminata',
  api_key_toggle: 'API Key attivata/disattivata',
  payment_success: 'Pagamento riuscito',
  payment_failed: 'Pagamento fallito',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    action: '',
    status: '',
    from_date: '',
    to_date: ''
  });

  useEffect(() => {
    loadData();
  }, [page, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsRes, statsRes, actionsRes] = await Promise.all([
        auditAPI.getLogs({ page, limit: 25, ...filters }),
        auditAPI.getStats(),
        auditAPI.getActions()
      ]);
      setLogs(logsRes.data.logs);
      setTotalPages(logsRes.data.pages);
      setStats(statsRes.data);
      setActions(actionsRes.data.actions);
    } catch (err) {
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare i log più vecchi di 90 giorni?')) return;
    try {
      const res = await auditAPI.cleanup();
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel cleanup');
    }
  };

  const resetFilters = () => {
    setFilters({ action: '', status: '', from_date: '', to_date: '' });
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }} data-testid="audit-logs-title">
            <Shield className="w-8 h-8 inline mr-3 text-blue-500" />
            Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">Registro delle azioni critiche nel sistema (retention: 90 giorni)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading} data-testid="refresh-logs">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button variant="destructive" onClick={handleCleanup} data-testid="cleanup-logs">
            <Trash2 className="w-4 h-4 mr-2" />
            Cleanup
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Log Totali</p>
              <p className="text-2xl font-bold" data-testid="total-logs">{stats.total_logs}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Oggi</p>
              <p className="text-2xl font-bold">{stats.logs_today}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Errori Oggi</p>
              <p className="text-2xl font-bold text-destructive">{stats.failures_today}</p>
            </CardContent>
          </Card>
          <Card className="border-border bg-card">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Retention</p>
              <p className="text-2xl font-bold">{stats.retention_days} giorni</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filtri
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Azione</Label>
              <Select 
                value={filters.action} 
                onValueChange={(v) => { setFilters({ ...filters, action: v === 'all' ? '' : v }); setPage(1); }}
              >
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">Tutte</SelectItem>
                  {actions.map(a => (
                    <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Stato</Label>
              <Select 
                value={filters.status} 
                onValueChange={(v) => { setFilters({ ...filters, status: v === 'all' ? '' : v }); setPage(1); }}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="success">Successo</SelectItem>
                  <SelectItem value="failure">Errore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Da</Label>
              <Input 
                type="date" 
                value={filters.from_date}
                onChange={(e) => { setFilters({ ...filters, from_date: e.target.value }); setPage(1); }}
                data-testid="filter-from-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">A</Label>
              <Input 
                type="date" 
                value={filters.to_date}
                onChange={(e) => { setFilters({ ...filters, to_date: e.target.value }); setPage(1); }}
                data-testid="filter-to-date"
              />
            </div>
            <div className="flex items-end">
              <Button variant="ghost" onClick={resetFilters} className="w-full">
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-[140px]">Data/Ora</TableHead>
                <TableHead>Azione</TableHead>
                <TableHead>Utente</TableHead>
                <TableHead>Risorsa</TableHead>
                <TableHead>IP</TableHead>
                <TableHead className="w-[80px]">Stato</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nessun log trovato
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => {
                  const Icon = ACTION_ICONS[log.action] || Shield;
                  return (
                    <TableRow key={log.log_id} data-testid={`log-${log.log_id}`}>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDate(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{ACTION_LABELS[log.action] || log.action}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.user_email || log.user_id || '-'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {log.resource_type && `${log.resource_type}: ${log.resource_id?.substring(0, 12) || ''}`}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {log.ip_address || '-'}
                      </TableCell>
                      <TableCell>
                        {log.status === 'success' ? (
                          <Badge className="bg-green-500/15 text-green-500 border-green-500/20">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            OK
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/15 text-red-500 border-red-500/20">
                            <XCircle className="w-3 h-3 mr-1" />
                            Errore
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.details && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <span className="text-xs">...</span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 bg-popover border-border">
                              <div className="text-xs">
                                <p className="font-semibold mb-2">Dettagli</p>
                                <pre className="bg-secondary/50 p-2 rounded overflow-auto max-h-40">
                                  {JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Pagina {page} di {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
